import { useState } from 'react';
import { api, ApiError, type Me } from '../api.js';
import { Loading, Page, useApi } from '../components.js';
import { href } from '../router.js';

interface Token { id: string; label: string; role: string; prefix: string; createdAt: string; lastUsedAt: string | null; revokedAt: string | null }

export function SettingsPage({ me, exp }: { me: Me; exp?: string }) {
  const tokens = useApi<{ tokens: Token[]; mcpUrl: string }>('/api/tokens');
  const members = useApi<{ members: { id: string; name: string; email: string; role: string }[] }>(exp ? `/api/expeditions/${exp}/members` : null);
  const planb = useApi<{ connection: { baseUrl: string; tokenHint: string } | null }>(exp ? `/api/expeditions/${exp}/planb` : null);
  const [label, setLabel] = useState('');
  const [role, setRole] = useState('journal');
  const [shown, setShown] = useState<string | null>(null);
  const [memberEmail, setMemberEmail] = useState('');
  const [pb, setPb] = useState({ baseUrl: 'https://planb.mototwin.app', token: '' });
  const [msg, setMsg] = useState<string | null>(null);
  const [pw, setPw] = useState({ current: '', next: '', repeat: '' });
  const [pwMsg, setPwMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const changePassword = async () => {
    if (pw.next !== pw.repeat) return setPwMsg({ ok: false, text: 'The new passwords don’t match.' });
    try {
      await api('POST', '/api/auth/password', { currentPassword: pw.current, newPassword: pw.next });
      setPw({ current: '', next: '', repeat: '' });
      setPwMsg({ ok: true, text: 'Password changed. Your other devices have been signed out.' });
    } catch (e) {
      setPwMsg({ ok: false, text: (e as ApiError).message });
    }
  };
  const guard = async (fn: () => Promise<unknown>) => { try { await fn(); setMsg(null); } catch (e) { setMsg((e as ApiError).message); } };

  return (
    <Page title="Settings" back={exp ? { href: href.expedition(exp), label: 'Expedition' } : { href: href.home(), label: 'Expeditions' }}>
      <p className="muted">Signed in as {me.name} ({me.email})</p>
      {msg && <p className="error" role="alert">{msg}</p>}
      <h2>Agent tokens</h2>
      <p className="muted">Each token has one role. Hermes needs <b>journal</b>; Codex planning needs <b>planning</b>. MCP address: <code>{tokens.data?.mcpUrl}</code></p>
      <form onSubmit={(e) => { e.preventDefault(); void guard(async () => { const t = await api<{ token: string }>('POST', '/api/tokens', { label, role }); setShown(t.token); setLabel(''); tokens.refetch(); }); }}>
        <label>Label<input value={label} onChange={(e) => setLabel(e.target.value)} required /></label>
        <label>Role<select value={role} onChange={(e) => setRole(e.target.value)}>{['journal', 'planning', 'edit', 'read'].map((r) => <option key={r} value={r}>{r}</option>)}</select></label>
        <button type="submit">Create token</button>
      </form>
      {shown && <p className="token">{shown}<br /><small>Copy it now; it is shown only once.</small></p>}
      {!tokens.data ? <Loading error={tokens.error} /> : (
        <ul className="list">
          {tokens.data.tokens.map((t) => (
            <li key={t.id}>{t.label} <span className="muted">· {t.role} · {t.prefix}… · {t.revokedAt ? 'revoked' : t.lastUsedAt ? `used ${t.lastUsedAt.slice(0, 10)}` : 'never used'}</span>
              {!t.revokedAt && <button className="secondary" onClick={() => void guard(async () => { await api('DELETE', `/api/tokens/${t.id}`); tokens.refetch(); })}>Revoke</button>}
            </li>
          ))}
        </ul>
      )}
      {exp && (
        <>
          <h2>Members of {exp}</h2>
          {members.data ? <ul className="list">{members.data.members.map((m) => <li key={m.id}>{m.name} <span className="muted">· {m.email} · {m.role}</span></li>)}</ul> : <Loading error={members.error} />}
          <form onSubmit={(e) => { e.preventDefault(); void guard(async () => { await api('POST', `/api/expeditions/${exp}/members`, { email: memberEmail }); setMemberEmail(''); members.refetch(); }); }}>
            <label>Add member by e-mail<input type="email" value={memberEmail} onChange={(e) => setMemberEmail(e.target.value)} required /></label>
            <button type="submit">Add member</button>
          </form>
          <h2>PlanB connection</h2>
          <p className="muted">{planb.data?.connection ? `Connected to ${planb.data.connection.baseUrl} (token ${planb.data.connection.tokenHint})` : 'Not connected.'}</p>
          <form onSubmit={(e) => { e.preventDefault(); void guard(async () => { await api('PUT', `/api/expeditions/${exp}/planb`, pb); setPb({ ...pb, token: '' }); planb.refetch(); }); }}>
            <label>PlanB address<input type="url" value={pb.baseUrl} onChange={(e) => setPb({ ...pb, baseUrl: e.target.value })} required /></label>
            <label>PlanB agent token<input type="password" value={pb.token} onChange={(e) => setPb({ ...pb, token: e.target.value })} required /></label>
            <button type="submit">Save PlanB connection</button>
          </form>
        </>
      )}
      <h2>Password</h2>
      <form onSubmit={(e) => { e.preventDefault(); void changePassword(); }}>
        <input type="email" autoComplete="username" value={me.email} readOnly hidden />
        <label>Current password<input type="password" autoComplete="current-password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} required /></label>
        <label>New password<input type="password" autoComplete="new-password" minLength={10} value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} required /></label>
        <label>Repeat new password<input type="password" autoComplete="new-password" minLength={10} value={pw.repeat} onChange={(e) => setPw({ ...pw, repeat: e.target.value })} required /></label>
        <p className="muted">At least 10 characters. Changing it signs you out on your other devices.</p>
        {pwMsg && (pwMsg.ok ? <p role="status">{pwMsg.text}</p> : <p className="error" role="alert">{pwMsg.text}</p>)}
        <button type="submit">Change password</button>
      </form>
      <h2>Sign out</h2>
      <button className="secondary" onClick={() => void guard(async () => { await api('POST', '/api/auth/logout'); window.location.hash = ''; window.location.reload(); })}>Sign out</button>
    </Page>
  );
}
